import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Store, User, Phone, Lock, Eye, EyeOff, MapPin, Home, Loader2,
  ShieldCheck, ArrowRight, CheckCircle2, KeyRound,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const Shell = ({ children }) => (
  <div
    data-testid="register-page"
    className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-100 px-4 py-10"
  >
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="w-full max-w-md"
    >
      <div className="bg-white rounded-2xl shadow-xl shadow-blue-900/10 border border-blue-100 px-7 py-9 sm:px-10 sm:py-11">
        <div className="flex flex-col items-center text-center">
          <img src="/slm-logo.png" alt="SLM - Sri Lakshmi Agencies" className="h-20 w-auto object-contain select-none" draggable={false} />
          <h2 className="mt-4 text-xl font-bold tracking-tight text-blue-800">Sri Lakshmi Agencies</h2>
          <p className="text-sm font-medium text-slate-500">Ramakuppam</p>
        </div>
        <div className="my-7 h-px w-full bg-gradient-to-r from-transparent via-blue-100 to-transparent" />
        {children}
      </div>
    </motion.div>
  </div>
);

const field =
  "w-full rounded-lg border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100";

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 phone, 2 otp, 3 details, 4 done
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [shopName, setShopName] = useState("");
  const [name, setName] = useState("");
  const [place, setPlace] = useState("");
  const [address, setAddress] = useState("");
  const [password, setPassword] = useState("1234");
  const [showPassword, setShowPassword] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/send-otp", { phone });
      setDevOtp(data.dev_otp || "");
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/verify-otp", { phone, code: otp });
      setStep(3);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitDetails = async (e) => {
    e.preventDefault();
    if (!shopName.trim() || !name.trim() || !phone.trim()) {
      setError("Customer Name, Shop Name and Mobile Number are required.");
      return;
    }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/register", {
        shop_name: shopName, name, phone, place, address, password, code: otp,
      });
      setStep(4);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  // ---- Step 4: success ----
  if (step === 4) {
    return (
      <Shell>
        <div data-testid="register-success" className="flex flex-col items-center text-center">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-9 w-9 text-emerald-600" />
          </div>
          <h1 className="mt-4 text-lg font-bold text-slate-800">Registration successful</h1>
          <p className="mt-2 text-sm text-slate-600">
            Waiting for <span className="font-semibold text-blue-700">Sri Lakshmi Agencies</span> approval.
            You'll be able to log in once your account is approved.
          </p>
          <button
            data-testid="go-to-login-button"
            onClick={() => navigate("/login")}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* progress */}
      <div className="mb-5 flex items-center justify-center gap-2">
        {[1, 2, 3].map((s) => (
          <span
            key={s}
            className={`h-1.5 w-10 rounded-full transition ${step >= s ? "bg-blue-600" : "bg-slate-200"}`}
          />
        ))}
      </div>

      {step === 1 && (
        <form onSubmit={sendOtp} className="space-y-4" data-testid="register-step-phone">
          <h1 className="text-center text-lg font-semibold text-slate-800">Create your account</h1>
          <p className="text-center text-sm text-slate-500">Enter your mobile number to get started</p>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Mobile Number</label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
              <input
                data-testid="register-phone-input"
                type="tel" inputMode="numeric" required autoFocus
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your 10-digit mobile number" className={field}
              />
            </div>
          </div>
          {error && <p data-testid="register-error" className="text-sm font-medium text-red-600">{error}</p>}
          <button
            data-testid="send-otp-button" type="submit" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700 disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send OTP <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={verifyOtp} className="space-y-4" data-testid="register-step-otp">
          <h1 className="text-center text-lg font-semibold text-slate-800">Verify your mobile</h1>
          <p className="text-center text-sm text-slate-500">
            Enter the OTP sent to <span className="font-semibold text-slate-700">{phone}</span>
          </p>
          {devOtp && (
            <div data-testid="demo-otp-note" className="flex items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <ShieldCheck className="h-4 w-4" /> Demo OTP: <span className="font-bold tracking-widest">{devOtp}</span>
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">OTP</label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
              <input
                data-testid="register-otp-input"
                type="text" inputMode="numeric" required autoFocus maxLength={6}
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter 6-digit OTP" className={`${field} tracking-[0.4em]`}
              />
            </div>
          </div>
          {error && <p data-testid="register-error" className="text-sm font-medium text-red-600">{error}</p>}
          <button
            data-testid="verify-otp-button" type="submit" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700 disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Verify & Continue <ArrowRight className="h-4 w-4" /></>}
          </button>
          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={() => { setStep(1); setOtp(""); setError(""); }} className="font-semibold text-slate-500 hover:text-blue-600">Change number</button>
            <button type="button" onClick={sendOtp} className="font-semibold text-blue-600 hover:underline">Resend OTP</button>
          </div>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={submitDetails} className="space-y-4" data-testid="register-step-details">
          <h1 className="text-center text-lg font-semibold text-slate-800">Your shop details</h1>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Shop Name <span className="text-red-500">*</span></label>
            <div className="relative">
              <Store className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
              <input data-testid="register-shopname-input" type="text" required value={shopName} onChange={(e) => setShopName(e.target.value)} placeholder="Your shop / business name" className={field} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Customer Name <span className="text-red-500">*</span></label>
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
              <input data-testid="register-name-input" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={field} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Place / Area <span className="text-slate-400">(optional)</span></label>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
              <input data-testid="register-place-input" type="text" value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Village / Town / Area" className={field} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Full Address <span className="text-slate-400">(optional)</span></label>
            <div className="relative">
              <Home className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-blue-400" />
              <textarea data-testid="register-address-input" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full delivery address" className={`${field} pt-3`} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Set PIN <span className="text-red-500">*</span></label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
              <input data-testid="register-password-input" type={showPassword ? "text" : "password"} required value={password} inputMode="numeric" onChange={(e) => setPassword(e.target.value.replace(/[^0-9]/g, ""))} placeholder="At least 4 digits (e.g. 1234)" className={`${field} pr-11`} />
              <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-blue-500" aria-label={showPassword ? "Hide password" : "Show password"}>
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <p data-testid="register-default-pin-hint" className="mt-1 text-xs text-slate-500">Default PIN is 1234. You can change it now or later from the Store.</p>
          </div>
          {error && <p data-testid="register-error" className="text-sm font-medium text-red-600">{error}</p>}
          <button
            data-testid="register-submit-button" type="submit" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700 disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Account"}
          </button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-slate-600">
        Already have an account?{" "}
        <Link data-testid="back-to-login-link" to="/login" className="font-semibold text-blue-600 transition hover:text-blue-700 hover:underline">Login</Link>
      </p>
    </Shell>
  );
}
